import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, UniqueIdentifier, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { SortableListProps, ListItemType, SortableListItemType } from "../List.types";
import { RootList } from './RootList';
import { SortableItem } from './SortableListItem';

/**
 * A sortable list component that allows drag-and-drop reordering of items.
 * 
 * @template T - The type of the list items, which must include an `id` property.
 * @param {SortableListProps<T>} props - The properties for the sortable list.
 * @returns {JSX.Element} The rendered sortable list.
 */
export const SortableList = <T extends ListItemType>(props: SortableListProps<T>) => {
  const {
    data, 
    updateSortOrder,
    component,
    keyExtractor,
    ...rootProps
  } = props;

  // Transform data to ensure all items have required IDs
  const [mappedData, setMappedData] = useState<(SortableListItemType)[]>([]);

  useEffect(() => {
    const newMappedData = data?.map((item, index) => {
      let itemId = item.id;
      if (itemId === undefined) {
        if (keyExtractor) {
          itemId = keyExtractor(item, index);
        } else {
          itemId = index;
        }
      }
      return {
        ...item,
        id: itemId,
      };
    }) || [];
    setMappedData(newMappedData);
  }, [data, keyExtractor]);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>();
  
  /**
   * Handles the drag start event, setting the active item ID.
   * 
   * @param {DragStartEvent} event - The drag start event.
   */
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  /**
   * Handles the drag end event, updating the sort order if necessary.
   * 
   * @param {DragEndEvent} event - The drag end event.
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const mappedDataIndex = mappedData.map((item) => item.id);
      const oldIndex = mappedDataIndex.indexOf(active.id);
      const newIndex = mappedDataIndex.indexOf(over.id);
      const movedData = arrayMove(mappedData, oldIndex, newIndex);
      
      
      // Call the callback if provided
      if (updateSortOrder) {
        // unforunately, the type of the updateSortOrder function is not known at this point
        // so we need to cast it to the correct type.
        // the only difference between the `mappedData` and the `data` is that the `mappedData` has a required id property
        const updatedData = updateSortOrder(movedData as T[], oldIndex, newIndex) as SortableListItemType[];
        if (updatedData) {
          setMappedData(updatedData);
        }
      } else {
        setMappedData(movedData);
      }
    }

    setActiveId(null);
  }
  const horizontalSort = props.align === 'Horizontal';

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[horizontalSort ? restrictToHorizontalAxis : restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext
        items={mappedData}
        strategy={horizontalSort ? horizontalListSortingStrategy : verticalListSortingStrategy}
      >
        <RootList {...rootProps}>
          {mappedData.map((item) => (
            <SortableItem
              key={item.id}
              id={item.id}
              item={item}
              component={component}
            />
          ))}
        </RootList>
      </SortableContext>
      
      {createPortal(
        <DragOverlay dropAnimation={null}>
          <div style={{ visibility: activeId ? 'visible' : 'hidden' }}>
            {/** Overlay content here */}
          </div>
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  )
}